import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { merge } from 'lodash';
import ReactApexChart from 'react-apexcharts';
import { BaseOptionChart } from './charts';

const STRATEGY_CONFIG = [
  { key: 'control_scheduled', name: 'Scheduled', color: '#2196F3' },
  { key: 'control_actual', name: 'Actual', color: '#1565C0' },
  { key: 'dumb', name: 'Dumb', color: '#9E9E9E' },
  { key: 'oracle', name: 'Oracle', color: '#4CAF50' },
  { key: 'prediction_only', name: 'Pred-Only', color: '#FF9800' },
];

export default function ProfileOverlayChart({ evaluation, priceSignal = [] }) {
  if (!evaluation?.strategies) return null;

  const strategies = evaluation.strategies;
  // Fallback: old evaluations stored a single "control" key
  if (strategies.control && !strategies.control_scheduled) {
    strategies.control_scheduled = strategies.control;
  }
  const startIdx = evaluation.price_signal_start_index || 0;

  // Build series for each strategy's charge profile
  const series = [];
  const colors = [];

  for (const { key, name, color } of STRATEGY_CONFIG) {
    const profile = strategies[key]?.charge_profile;
    if (Array.isArray(profile)) {
      series.push({
        name,
        type: 'line',
        data: profile.map((val, i) => ({ x: i, y: parseFloat(val.toFixed(2)) })),
      });
      colors.push(color);
    }
  }

  // Add HFP price on secondary y-axis
  const hasPriceData = Array.isArray(priceSignal) && priceSignal.length > 0;
  if (hasPriceData) {
    const numSlots = series[0]?.data?.length || 0;
    const priceSlice = priceSignal.slice(startIdx, startIdx + numSlots);
    series.push({
      name: 'HFP Price',
      type: 'line',
      data: priceSlice.map((val, i) => ({ x: i, y: parseFloat(val.toFixed(4)) })),
    });
    colors.push('#E91E63');
  }

  const yAxes = [
    {
      title: { text: 'Power (kW)' },
      labels: { formatter: (v) => (v != null ? `${v.toFixed(1)} kW` : v) },
    },
  ];

  if (hasPriceData) {
    yAxes.push({
      opposite: true,
      title: { text: 'Price ($/kWh)' },
      labels: { formatter: (v) => (v != null ? `$${v.toFixed(3)}` : v) },
    });
  }

  // Build stroke widths: 2 for profiles, 1 dashed for price
  const strokeWidths = series.map((_, i) => (i < STRATEGY_CONFIG.length ? 2 : 1));
  const dashArray = series.map((_, i) => (i < STRATEGY_CONFIG.length ? 0 : 5));

  const chartOptions = merge(BaseOptionChart(), {
    colors,
    stroke: { width: strokeWidths, curve: 'straight', dashArray },
    dataLabels: { enabled: false },
    xaxis: {
      title: { text: '15-min Slot Index' },
      type: 'numeric',
      labels: { formatter: (v) => Math.round(v) },
    },
    yaxis: yAxes,
    tooltip: {
      shared: true,
      intersect: false,
    },
    legend: { show: true, position: 'top' },
  });

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          Charge Profile Overlay — {evaluation.vin ? evaluation.vin.slice(-6) : 'Session'}
        </Typography>
        <ReactApexChart type="line" series={series} options={chartOptions} height={320} />
      </CardContent>
    </Card>
  );
}
